import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Building2, ChevronDown, Check } from "lucide-react";

/**
 * Fase 14.2 · Tenant Switcher
 * Muestra un dropdown con los tenants del user y permite hacer switch al activo.
 * Solo se renderiza si el user tiene 2+ tenants.
 */
export default function TenantSwitcher({ variant = "topbar" }) {
  const { user, refreshUser } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!user) return;
    api.get("/auth/my-tenants").then((r) => {
      setTenants(r.data.items || []);
      setActive(r.data.active_tenant_id);
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const activeTenant = tenants.find((t) => t.id === active);

  const doSwitch = async (slug) => {
    setSwitching(true);
    try {
      const { data } = await api.post("/auth/switch-tenant", { tenant_slug: slug });
      // Persist new token
      localStorage.setItem("dcc_access_token", data.access_token);
      setActive(data.active_tenant_id);
      setOpen(false);
      // Trigger a soft-reload of context
      if (refreshUser) await refreshUser();
      // Reload current view data (best-effort)
      window.location.reload();
    } catch (e) {
      console.error(e);
    } finally { setSwitching(false); }
  };

  if (tenants.length < 2) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="tenant-switcher-dropdown"
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-gray-700 hover:border-brand-orange-500 text-sm transition-colors ${variant === "topbar" ? "bg-[#111827]" : "bg-transparent"}`}>
        {activeTenant?.branding?.primary_color && (
          <span className="w-2 h-2 rounded-full" style={{ background: activeTenant.branding.primary_color }} />
        )}
        <Building2 className="w-4 h-4 text-gray-400" />
        <span className="text-white font-semibold" data-testid="tenant-switcher-active-name">
          {activeTenant?.name || "Sin tenant"}
        </span>
        <span className="ml-1 text-[10px] font-bold uppercase tracking-widest text-brand-orange-500">
          {tenants.length}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-72 bg-[#111827] border border-gray-800 rounded-lg shadow-2xl z-50 overflow-hidden"
          data-testid="tenant-switcher-menu">
          <div className="p-3 border-b border-gray-800">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Tenants disponibles</p>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {tenants.map((t) => (
              <button
                key={t.id}
                onClick={() => doSwitch(t.slug)}
                disabled={switching || t.id === active}
                data-testid={`tenant-switcher-option-${t.slug}`}
                className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-[#0B0F14] transition-colors text-left ${
                  t.id === active ? "bg-brand-orange-500/10" : ""
                }`}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: t.branding?.primary_color || "#DC5A0F" }}>
                  <Building2 className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{t.name}</div>
                  <div className="text-[10px] text-gray-500 font-mono truncate">/t/{t.slug} · {t.type}</div>
                </div>
                {t.id === active && <Check className="w-4 h-4 text-brand-orange-500 flex-shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
